import useChangeBaseRole from 'api/users/useChangeBaseRole/useChangeBaseRole';
import useConvertToAgent from 'api/users/useConvertToAgent/useConvertToAgent';
import useDeleteAccount from 'api/users/useDeleteAccount/useDeleteAccount';
import useEditUser from 'api/users/useEditUser/useEditUser';
import useInsertUser from 'api/users/useInsertUser/useInsertUser';
import useResetPassword from 'api/users/useResetPassword/useResetPassword';
import useUserChangeActivationState from 'api/users/useUserChangeActivationState/useUserChangeActivationState';
import useUserList from 'api/users/useUserList/useUserList';
import ConvertUserToAgentModal from 'components/private/Modals/users/ConvertUserToAgentModal/ConvertUserToAgentModal';
import UserChangeBaseRoleModal from 'components/private/Modals/users/UserChangeBaseRoleModal/UserChangeBaseRoleModal';
import UserEditModal from 'components/private/Modals/users/UserEditModal/UserEditModal';
import UserInsertModal from 'components/private/Modals/users/UserInsertModal/UserInsertModal';
import UserResetPasswordModal from 'components/private/Modals/users/UserResetPasswordModal/UserResetPasswordModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import useUserTableColumns from 'hooks/users/useUsersTableColumns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PageSpinner from '../../components/shared/pageSpinner/PageSpinner';

// eslint-disable-next-line max-statements
const UsersPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);
  const [getUserList, loading, userList, pageRef] = useUserList();

  const [activeModal, setActiveModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState({});

  const { editRequest, editLoading } = useEditUser();
  const { insertRequest, insertLoading } = useInsertUser();
  const { changeBaseRoleRequest, changeBaseRoleLoading } = useChangeBaseRole();
  const { changeActivationStateRequest } = useUserChangeActivationState();
  const { deleteAccountRequest, deleteAccountLoading } = useDeleteAccount();
  const { resetPasswordRequest, resetPasswordLoading } = useResetPassword();
  const { convertToAgentRequest, convertToAgentLoading } = useConvertToAgent();

  const tableRef = useRef(null);
  const { can } = usePermissions();

  const usersPermissions =
    permissions.general.categories.globalSettings.usersManagement.users;

  const canCreate = can(usersPermissions.create);
  const canEdit = can(usersPermissions.edit);
  const canDelete = can(usersPermissions.delete);
  const canChangePassword = can(usersPermissions.changePassword);
  const canChangeBaseRole = can(usersPermissions.changeBaseRole);
  const canChangeStatus = can(usersPermissions.changeStatus);
  const canViewRoles = can(usersPermissions.roles);
  const canViewPermissions = can(usersPermissions.permissions);
  const canSetAsAgent = can(usersPermissions.setAsAgent);

  useEffect(() => {
    getUserList({ page: 1, count: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getUserList({ page, count: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const openModal = useCallback(
    modal => row => () => {
      setSelectedUser(row);
      setActiveModal(modal);
    },
    [],
  );

  const closeModal = useCallback(() => setActiveModal(null), []);

  const changeActivationStateItem = useCallback(
    row => () => {
      changeActivationStateRequest(
        {
          guid: row.id,
          isActive: row.activationState !== 'فعال',
        },
        () => getUserList({ page, count: pageSize }),
      );
    },
    [changeActivationStateRequest],
  );

  const handleConvertToAgent = ({ zoneGUID }) => {
    convertToAgentRequest({
      userGUID: selectedUser.guid,
      zoneGUID,
      onSuccess: () => {
        getUserList({ page, count: pageSize });
        closeModal();
      },
    });
  };

  const onShowRoles = useCallback(
    row => () => {
      navigate(`/users/${row.id}/role-list`);
    },
    [navigate],
  );

  const onShowPermissions = useCallback(
    row => () => {
      navigate(`/users/${row.id}/permission-list`);
    },
    [navigate],
  );

  const [columnsData] = useUserTableColumns({
    editItem: openModal('edit'),
    changePasswordItem: openModal('changePassword'),
    changeBaseRoleItem: openModal('changeBaseRole'),
    onDeleteItem: openModal('delete'),
    convertToAgentItem: openModal('convertToAgent'),
    apiRef: tableRef,
    onShowRoles,
    onShowPermissions,
    changeActivationStateItem,
    permissions: {
      canEdit,
      canDelete,
      canChangePassword,
      canChangeBaseRole,
      canChangeStatus,
      canViewRoles,
      canViewPermissions,
      canSetAsAgent,
    },
  });

  const editHandle = data => {
    if (data) {
      editRequest(
        {
          guid: selectedUser.id,
          mobile: data.mobile,
          name: data.name,
          family: data.family,
        },
        () => getUserList({ page, count: pageSize }),
        closeModal,
      );
    }
  };

  const insertHandle = payload => {
    insertRequest(() => getUserList({ page, count: pageSize }), closeModal, {
      roleGUID: payload.roleGUID,
      mobile: payload.mobile,
      name: payload.name,
      family: payload.family,
      password: payload.password,
    });
  };

  const changePasswordHandle = ({ password }) => {
    if (!password) return;

    resetPasswordRequest({
      guid: selectedUser.id,
      password,
      onSuccess: () => {
        getUserList({ page, count: pageSize });
        closeModal();
      },
    });
  };

  const changeBaseRoleHandle = data => {
    if (data) {
      changeBaseRoleRequest(
        {
          userGUID: data.userGUID,
          roleGUID: data.roleGUID,
        },
        () => getUserList({ page, count: pageSize }),
        closeModal,
      );
    }
  };

  const onDeleteHandle = useCallback(() => {
    deleteAccountRequest({
      Guid: selectedUser.id,
      onSuccess: () => {
        getUserList({ page, count: pageSize });
        closeModal();
      },
    });
  }, [deleteAccountRequest, selectedUser.id, closeModal]);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    getUserList({ page: pageNumber, count: pageSize });
  }

  return (
    <>
      <div>{pageRef.refTitle}</div>
      {Object.keys(userList).length !== 0 ? (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={userList[page]}
          rows={userList[page]}
          pageSize={pageSize}
          isDeletable={canDelete}
          isLoading={loading}
          addLable={canCreate ? 'ایجاد کاربر جدید' : undefined}
          onAddClick={canCreate ? () => setActiveModal('insert') : undefined}
          setPageSize={val => setPageSize(val)}
          apiRef={tableRef}
          onPageChange={p => getPageData(p)}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef.totalCount / pageSize) : 1
          }
          setSelectedRows={rows => setSelectedRows(rows)}
          selectedRows={selectedRows}
        />
      ) : (
        <PageSpinner loading />
      )}

      {canEdit ? (
        <UserEditModal
          open={activeModal === 'edit'}
          handleClose={closeModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={selectedUser}
        />
      ) : null}

      {canCreate ? (
        <UserInsertModal
          open={activeModal === 'insert'}
          handleClose={closeModal}
          insertLoading={insertLoading}
          onInsert={insertHandle}
        />
      ) : null}

      {canChangePassword ? (
        <UserResetPasswordModal
          open={activeModal === 'changePassword'}
          handleClose={closeModal}
          changePasswordLoading={resetPasswordLoading}
          onChangePassword={changePasswordHandle}
        />
      ) : null}

      {canChangeBaseRole ? (
        <UserChangeBaseRoleModal
          open={activeModal === 'changeBaseRole'}
          handleClose={closeModal}
          changeBaseRoleLoading={changeBaseRoleLoading}
          onChangeBaseRole={changeBaseRoleHandle}
          userGUID={selectedUser.id}
        />
      ) : null}

      <ConvertUserToAgentModal
        open={activeModal === 'convertToAgent'}
        handleClose={closeModal}
        convertToAgentLoading={convertToAgentLoading}
        onConvertToAgent={handleConvertToAgent}
      />

      {canDelete ? (
        <DeleteModal
          title={`${selectedUser.name} ${selectedUser.family}`}
          open={activeModal === 'delete'}
          handleClose={closeModal}
          deleteLoading={deleteAccountLoading}
          onDelete={onDeleteHandle}
        />
      ) : null}
    </>
  );
};

export default UsersPage;
